import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Checkbox, LinearProgress, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import { fetchPayrollDetail, reduceSubmitPayroll } from "store/slices/payroll";
import { fetchListReimbursement, reduceRejectReimbursement } from "store/slices/reimbursement";

import PayrollBookingTable from './PayrollBookingTable';
import AppTable from '../Table/AppTable';
import ImageIcon from "assets/icons/image-regular.svg";
import moment from "moment-timezone";
import AppUtil from 'utilities/AppUtil';
import AppConfig from 'config/AppConfig';

const headCells = [
  {
    id: "name",
    numeric: "left",
    disablePadding: false,
    label: "label.name",
    sort: true,
  },
  {
    id: "date",
    numeric: "left",
    disablePadding: false,
    label: "label.date",
    sort: true,
  },
  {
    id: "price",
    numeric: "left",
    disablePadding: false,
    label: "label.price",
    sort: true,
  },
  {
    id: "picture",
    numeric: "left",
    disablePadding: false,
    label: "label.picture",
    sort: true,
  },
  {
    id: "reject",
    numeric: "left",
    disablePadding: false,
    label: "label.reject",
    sort: true,
  }
];

const useStyles = makeStyles(() => ({
  headerSummary: {
    backgroundColor: AppColor.mainColor,
    borderRadius: 8,
    color: AppColor.white,
    fontWeight: 700,
    '& .header-item': {
      padding: '0.5rem',
      textAlign: 'center',
    }
  },
  bodySummary: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    '& .summary-item:not(:last-child)': {
      borderBottom: '1px solid #e0e0e0'
    }
  },
  summaryItem: {
    padding: '0.5rem',
    textAlign: 'center'
  },
  image: {
    maxHeight: 50
  },
  textBold: {
    fontWeight: 700
  },
  tableReimbursement: {
    '& .MuiCheckbox-colorPrimary.Mui-checked': {
      color: AppColor.pink
    }
  }
}))

const PayrollForm = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payrollDetail, setPayrollDetail] = useState({});
  const [payrollReimbursements, setPayrollReimbursements] = useState([]);
  const [totalReimbursement, setTotalReimbursement] = useState(0);
  const [selected, setSelected] = useState([]);

  const getDetail = async () => {
    const result = await dispatch(fetchPayrollDetail({ id: id }));
    if (result.payload) {
      setPayrollDetail(result.payload);
    }
  };
  const getPayrollReimbursements = async () => {
    setLoading(true);
    const result = await dispatch(fetchListReimbursement({ payrollId: id }));
    setLoading(false);
    const { payload } = result;
    if (payload) {
      const { totalReimbursement, reimbursements } = payload;
      setTotalReimbursement(totalReimbursement);
      setPayrollReimbursements(reimbursements);
      const rejectReimbursement = reimbursements.filter(item => item.isReject).map(reimbursemen => {
        return reimbursemen.id;
      });
      setSelected(rejectReimbursement);
    }
  };

  useEffect(() => {
    getDetail();
    getPayrollReimbursements();
  }, []);

  const renderPicture = (picture) => {
    let imageSrc = picture ? picture : ImageIcon;
    return (
      <img src={imageSrc} className={classes.image} />
    )
  };

  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter(item => item !== id))
    }
  }
  const handleSubmitPayroll = async () => {
    setSubmitting(true);
    await dispatch(reduceSubmitPayroll({ id: id }));
    const params = {
      payrollId: id,
      rejectRequests: payrollReimbursements.map(item => {
        return {
          id: item.id,
          isReject: selected.includes(item.id)
        }
      })
    }
    payrollReimbursements.length > 0 && await dispatch(reduceRejectReimbursement(params));
    getDetail();
    getPayrollReimbursements();
    setSubmitting(false);
  }

  return (
    <Box>
      {
        payrollDetail && payrollDetail.payrollStatus === 'PAID' && (
          <Box display="flex" className="mb-2">
            <Box className="mr-1">{`${t('label.payrollID')}:`}</Box>
            <Box fontWeight={700}>{payrollDetail.xeroPayrollId}</Box>
          </Box>
        )
      }
      <PayrollBookingTable />
      <Box className='my-3'>
        <Typography variant="h5">
          {t("title.summary")}
        </Typography>
      </Box>
      <Box className={classes.headerSummary} display="flex">
        <Box className="header-item" flex="2 0 0px">{t("label.shift")}</Box>
        <Box className="header-item" flex="1 0 0px">{t("label.hours")}</Box>
        <Box className="header-item" flex="2 0 0px">{t("label.price")}</Box>
      </Box>
      <Box className={classes.bodySummary}>
        {
          payrollDetail.summaries && payrollDetail.summaries.map((item, index) => {
            return (
              <Box key={`payroll-summary-${index}`} display="flex" className="summary-item">
                <Box className={classes.summaryItem} flex="2 0 0px">{t(`shift.${item.shiftType}`)}</Box>
                <Box className={classes.summaryItem} flex="1 0 0px">{item.houses.toFixed(2)}</Box>
                <Box className={classes.summaryItem} flex="2 0 0px">{`$${AppUtil.formatMoney(item.price.toFixed(2))}`}</Box>
              </Box>
            )
          })
        }
      </Box>
      <Box className="my-3 text-right">
        <Box>
          <span className={classes.textBold}>{t('label.totalHours')}: </span>
          <span>{payrollDetail.totalHours && payrollDetail.totalHours.toFixed(2)}</span>
        </Box>
        <Box>
          <span className={classes.textBold}>{t('label.totalPrice')}: </span>
          <span>{`$${payrollDetail.totalPrice && AppUtil.formatMoney(payrollDetail.totalPrice.toFixed(2))}`}</span>
        </Box>
      </Box>
      <Box className='my-3'>
        <Typography variant="h5">
          {t("title.reimbursement")}
        </Typography>
      </Box>
      <Box className={classes.tableReimbursement}>
        <AppTable
          headCells={headCells}
          onRequestSort={() => null}
          order="asc"
          orderBy="id"
          rowCount={payrollReimbursements.length}
        >
          <TableBody>
            {loading && (<TableRow>
              <TableCell colSpan={7} padding="none">
                <LinearProgress />
              </TableCell>
            </TableRow>)}
            {payrollReimbursements.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{moment(row.date).format(AppConfig.DATE_FORMAT.DEFAULT)}</TableCell>
                <TableCell>{`$${AppUtil.formatMoney(row.price.toFixed(2))}`}</TableCell>
                <TableCell>{renderPicture(row.pictureAws3Link)}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={selected.indexOf(row.id) !== -1}
                    inputProps={{
                      'aria-labelledby': row.id
                    }}
                    onClick={() => _handleSelect(row.id)}
                    color="primary"
                  />
                </TableCell>
              </TableRow>
            ))}

            {payrollReimbursements.length === 0 > 0 && (
              <TableRow style={{ height: 53 }}>
                <TableCell colSpan={headCells.length + 1} className="text-center">
                  {t("label.no_data")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </AppTable>
      </Box>
      <Box className="my-3 text-right">
        <Box>
          <span className={classes.textBold}>{t('label.totalReimbursement')}: </span>
          <span>{`$${totalReimbursement && AppUtil.formatMoney(totalReimbursement.toFixed(2))}`}</span>
        </Box>
        <hr />
        <Box>
          <span className={classes.textBold}>{t('label.totalBilling')}: </span>
          <span>{`$${payrollDetail.totalBilling && AppUtil.formatMoney(payrollDetail.totalBilling.toFixed(2))}`}</span>
        </Box>
      </Box>
      <Box textAlign="right" className="mt-3">
        <AppButton
          label={t('label.back')}
          mode="outlined"
          onClick={() => history.push('/admin/payroll')}
          style={{ marginRight: 10 }}
        />
        <AppButton
          label={t('button.submit')}
          onClick={() => handleSubmitPayroll()}
          disabled={submitting}
          isLoading={submitting}
        />
      </Box>
    </Box>
  )
}

PayrollForm.propTypes = {
}

export default PayrollForm;
